/**
 * PATH: apps/quote/src/components/builder/panels/inspector/ProvenanceTab.tsx
 * PURPOSE: Price source / provenance tab for the row inspector
 */

import { useQuery } from '@tanstack/react-query'
import {
  Link2, BookOpen, FileText, History, Building2, Sparkles,
  Loader2, RefreshCw, ArrowRight,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteLineItem } from '@/lib/quoteApi'

function getPriceSourceIcon(type: string) {
  switch (type) {
    case 'brochure': return <BookOpen className="w-4 h-4 text-green-600" />
    case 'supplier_quote': return <FileText className="w-4 h-4 text-blue-600" />
    case 'historical': return <History className="w-4 h-4 text-purple-600" />
    case 'pricebook': return <Building2 className="w-4 h-4 text-amber-600" />
    case 'ai_suggested': return <Sparkles className="w-4 h-4 text-pink-600" />
    default: return <Link2 className="w-4 h-4 text-slate-500" />
  }
}

interface ProvenanceTabProps {
  lineItem: QuoteLineItem
  onApplyUpdate?: (newPrice: number) => void
}

export default function ProvenanceTab({ lineItem, onApplyUpdate }: ProvenanceTabProps) {
  const { data: priceSource, isLoading, refetch } = useQuery({
    queryKey: ['priceSource', lineItem.id],
    queryFn: () => quoteApi.getLineItemPriceSource(lineItem.id),
    staleTime: 60 * 1000,
  })

  const { data: alternatives = [] } = useQuery({
    queryKey: ['priceSuggestions', lineItem.description],
    queryFn: () => quoteApi.getAISuggestions(lineItem.description || ''),
    enabled: !!lineItem.description,
    staleTime: 60 * 1000,
  })

  const currentPrice = Number(lineItem.rate_per_unit) || 0

  return (
    <div className="p-3 space-y-4">
      {/* Current Price Source */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5 mb-2">
          <Link2 className="w-4 h-4 text-slate-500" />
          Price Source
        </h3>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : priceSource ? (
          <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-3">
              {getPriceSourceIcon(priceSource.source_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">{priceSource.source_display}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {priceSource.price_date ? `Updated: ${new Date(priceSource.price_date).toLocaleDateString()}` : 'Unknown date'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{formatCurrency(priceSource.price_value)}</p>
                <p className="text-xs text-slate-500">/{priceSource.price_unit}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className={cn(
                'px-2 py-0.5 text-xs font-medium rounded-full',
                priceSource.confidence >= 80
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : priceSource.confidence >= 50
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              )}>
                {priceSource.confidence}% confidence
              </span>
              <button onClick={() => refetch()} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Refresh">
                <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-center">
            <Link2 className="w-6 h-6 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
            <p className="text-sm text-slate-500">No price source linked</p>
            <p className="text-xs text-slate-400 mt-0.5">Price entered manually</p>
          </div>
        )}
      </div>

      {/* Price Comparison */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5 mb-2">
          <ArrowRight className="w-4 h-4 text-slate-500" />
          Price Comparison
        </h4>
        <div className="space-y-2">
          <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Current Price</span>
              <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                {formatCurrency(currentPrice)}/{lineItem.unit || 'm2'}
              </span>
            </div>
          </div>

          {alternatives.length > 0 ? (
            alternatives.slice(0, 4).map((alt) => {
              const priceDiff = alt.unit_price - currentPrice
              const priceDiffPercent = currentPrice > 0 ? (priceDiff / currentPrice) * 100 : 0
              return (
                <div key={alt.id} className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {getPriceSourceIcon(alt.type)}
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-900 dark:text-white truncate">{alt.source}</p>
                        <p className="text-xs text-slate-500 truncate">{alt.item_name}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{formatCurrency(alt.unit_price)}</p>
                      <p className={cn('text-xs font-medium', priceDiff > 0 ? 'text-red-600' : priceDiff < 0 ? 'text-green-600' : 'text-slate-500')}>
                        {priceDiff > 0 ? '+' : ''}{priceDiffPercent.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  {onApplyUpdate && Math.abs(priceDiff) > 0.01 && (
                    <button
                      onClick={() => onApplyUpdate(alt.unit_price)}
                      className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium border border-slate-200 dark:border-slate-600 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Apply this price
                    </button>
                  )}
                </div>
              )
            })
          ) : (
            <p className="text-xs text-slate-400 text-center py-2">No alternative prices found</p>
          )}
        </div>
      </div>

      {/* Reconciliation */}
      {priceSource && alternatives.length > 0 && (
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <button className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            <RefreshCw className="w-4 h-4" />
            Auto-reconcile to Latest Brochure Price
          </button>
          <p className="text-xs text-slate-400 text-center mt-1.5">
            Updates this item to match the supplier's current published price
          </p>
        </div>
      )}
    </div>
  )
}
